import { useEffect, useState } from "react";
import axios from "axios";
import { CalendarDays, Clock, MapPin, Search, Thermometer, Umbrella, Wind } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function scoreTone(score, isDarkMode) {
  if (score >= 8) return isDarkMode ? "text-emerald-300" : "text-emerald-700";
  if (score >= 6) return isDarkMode ? "text-lime-300" : "text-lime-700";
  if (score >= 4) return isDarkMode ? "text-amber-300" : "text-amber-700";
  return isDarkMode ? "text-orange-300" : "text-orange-700";
}

export function EventWeatherCard({ lat, lon, isDarkMode }) {
  const [eventName, setEventName] = useState("");
  const [locationQuery, setLocationQuery] = useState("");
  const [hoursAhead, setHoursAhead] = useState(24);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchEventWeather = async () => {
    if (!lat || !lon) return;
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(`${API}/weather/events/weather`, {
        params: {
          lat,
          lon,
          event_name: eventName.trim() || undefined,
          location_query: locationQuery.trim() || undefined,
          hours_ahead: hoursAhead,
        },
        timeout: 16000,
      });
      setData(response.data);
    } catch (requestError) {
      console.error("Event weather error:", requestError.message);
      setError("Event weather is temporarily unavailable.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEventWeather(); }, [lat, lon]);

  const panel = isDarkMode ? "bg-white/5 border-white/10" : "bg-white/80 border-gray-200";
  const muted = isDarkMode ? "text-white/55" : "text-gray-600";
  const guidance = data?.guidance || {};
  const current = data?.current || {};
  const timeline = data?.hourly || [];

  return (
    <div className="glass rounded-3xl p-4 lg:h-[420px] overflow-hidden" data-testid="event-weather-card">
      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-4 h-full">
        <aside className={`rounded-2xl border p-3 overflow-y-auto ${panel}`} data-testid="event-weather-input-panel">
          <div className="flex items-center gap-2 mb-3">
            <CalendarDays className="w-5 h-5 text-[#ff9500]" />
            <div>
              <h3 className={`font-bold ${isDarkMode ? "text-white/85" : "text-gray-900"}`}>Festival & Event Weather</h3>
              <p className={`text-xs ${muted}`}>Current through 1-24 hours</p>
            </div>
          </div>

          <label className={`text-xs font-semibold ${muted}`} htmlFor="event-weather-name-input">Event name</label>
          <input
            id="event-weather-name-input"
            data-testid="event-weather-name-input"
            value={eventName}
            onChange={(event) => setEventName(event.target.value)}
            placeholder="e.g. County fair"
            className={`w-full mt-1 mb-2 rounded-xl px-3 py-2 text-sm border ${isDarkMode ? "bg-black/30 border-white/10 text-white placeholder:text-white/35" : "bg-white border-gray-200 text-gray-900 placeholder:text-gray-400"}`}
          />

          <label className={`text-xs font-semibold ${muted}`} htmlFor="event-weather-location-input">Place</label>
          <input
            id="event-weather-location-input"
            data-testid="event-weather-location-input"
            value={locationQuery}
            onChange={(event) => setLocationQuery(event.target.value)}
            placeholder="Venue, park, city, address"
            className={`w-full mt-1 mb-2 rounded-xl px-3 py-2 text-sm border ${isDarkMode ? "bg-black/30 border-white/10 text-white placeholder:text-white/35" : "bg-white border-gray-200 text-gray-900 placeholder:text-gray-400"}`}
          />

          <label className={`text-xs font-semibold ${muted}`} htmlFor="event-weather-hours-input">Forecast window: {hoursAhead}h</label>
          <input
            id="event-weather-hours-input"
            data-testid="event-weather-hours-input"
            type="range"
            min="1"
            max="24"
            value={hoursAhead}
            onChange={(event) => setHoursAhead(Number(event.target.value))}
            className="w-full mt-2 mb-3 accent-[#ff9500]"
          />

          <button
            type="button"
            data-testid="event-weather-search-button"
            onClick={fetchEventWeather}
            disabled={loading}
            className="w-full rounded-xl bg-[#ff9500] text-black font-bold py-2.5 text-sm hover:bg-[#ffad33] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
          >
            <Search className="w-4 h-4" /> {loading ? "Checking..." : "Check event weather"}
          </button>
          {error && <p className="mt-3 text-xs text-orange-400" data-testid="event-weather-error-message">{error}</p>}
        </aside>

        <section className={`rounded-2xl border p-3 overflow-y-auto ${panel}`} data-testid="event-weather-detail-panel">
          {loading && !data ? (
            <div className="animate-pulse space-y-4" data-testid="event-weather-loading">
              <div className={`h-8 rounded-xl w-2/3 ${isDarkMode ? "bg-white/10" : "bg-gray-200"}`} />
              <div className="grid grid-cols-4 gap-2"><div className={`h-20 rounded-xl ${isDarkMode ? "bg-white/10" : "bg-gray-200"}`} /><div className={`h-20 rounded-xl ${isDarkMode ? "bg-white/10" : "bg-gray-200"}`} /><div className={`h-20 rounded-xl ${isDarkMode ? "bg-white/10" : "bg-gray-200"}`} /><div className={`h-20 rounded-xl ${isDarkMode ? "bg-white/10" : "bg-gray-200"}`} /></div>
              <div className={`h-20 rounded-xl ${isDarkMode ? "bg-white/10" : "bg-gray-200"}`} />
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className={`text-xs font-semibold ${isDarkMode ? "text-[#ff9500]" : "text-orange-700"}`}>{data?.event_name || "Festival / Event Weather"}</p>
                  <h3 className={`text-xl font-black truncate ${isDarkMode ? "text-white" : "text-gray-900"}`} data-testid="event-weather-location-label">{data?.location?.label || "Current location"}</h3>
                  <p className={`text-xs ${muted}`} data-testid="event-weather-source-label">{data?.source || "OpenWeatherMap"}</p>
                </div>
                <div className="text-right shrink-0" data-testid="event-weather-score">
                  <p className={`text-4xl font-black leading-none ${scoreTone(guidance.score, isDarkMode)}`}>{guidance.score || "—"}</p>
                  <p className={`text-[11px] font-bold ${muted}`}>{guidance.rating || "Loading"}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="event-weather-current-grid">
                <div className={`rounded-xl border p-2.5 ${panel}`}><Thermometer className="w-4 h-4 text-orange-400 mb-1" /><p className="text-xs opacity-60">Now</p><p className="font-bold">{current.air_temp_f ?? "—"}°F</p></div>
                <div className={`rounded-xl border p-2.5 ${panel}`}><Wind className="w-4 h-4 text-cyan-400 mb-1" /><p className="text-xs opacity-60">Wind</p><p className="font-bold">{current.wind_speed_mph ?? "—"} mph</p></div>
                <div className={`rounded-xl border p-2.5 ${panel}`}><Umbrella className="w-4 h-4 text-blue-400 mb-1" /><p className="text-xs opacity-60">Best</p><p className="font-bold">{guidance.best_window || "—"}</p></div>
                <div className={`rounded-xl border p-2.5 ${panel}`}><Clock className="w-4 h-4 text-lime-400 mb-1" /><p className="text-xs opacity-60">Window</p><p className="font-bold">{data?.hours_ahead || hoursAhead}h</p></div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="event-weather-guidance-grid">
                <div className={`rounded-xl border p-2.5 ${panel}`}>
                  <p className="text-xs font-semibold mb-2 text-orange-400">Weather watch</p>
                  {(guidance.cautions || []).slice(0, 3).map((item) => <p key={item} className={`text-xs mb-1 ${muted}`}>• {item}</p>)}
                </div>
                <div className={`rounded-xl border p-2.5 ${panel}`}>
                  <p className="text-xs font-semibold mb-2 text-lime-400">Bring / plan</p>
                  {(guidance.packing || []).slice(0, 3).map((item) => <p key={item} className={`text-xs mb-1 ${muted}`}>• {item}</p>)}
                </div>
              </div>

              <div className={`rounded-xl border p-2.5 ${panel}`} data-testid="event-weather-hourly-strip">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold">Hourly conditions</p>
                  <p className={`text-[11px] ${muted}`}>Now to +{data?.hours_ahead || hoursAhead}h</p>
                </div>
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {timeline.slice(0, 12).map((hour) => (
                    <div key={`${hour.offset_hour}-${hour.time}`} className={`min-w-[86px] rounded-xl border p-2 ${panel}`} data-testid={`event-hour-${hour.offset_hour}`}>
                      <p className="text-[11px] font-semibold truncate">{hour.time}</p>
                      <p className="text-lg font-black">{hour.temp_f ?? "—"}°</p>
                      <p className={`text-[11px] truncate ${muted}`}>{hour.precip_probability}% rain</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}